import { unset } from "lodash";
import { useCallback, useContext } from "react";
import { DeepPartial } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { None, Option, Some } from "space-monad";
import { captcha } from "../../../../components/captcha";
import { useFileLoader } from "../../../../components/hooks/file-loader";
import { useAuthedFields } from "../../../../store/launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { Field } from "../../../../store/launch-configurations/models/internal/field";
import { LaunchesDTO } from "../../../../store/launches/models/dto";
import { ParticipationsService } from "../../../../store/participations/service";
import { deepOmitBy } from "../../../../utils/utils";
import { ConfigContext } from "../config-context";

type FormType = LaunchesDTO.Requests.ParticipationForm.ForForm;

export interface HandleSubmitCallArgs {
  defaultValues: DeepPartial<any>;
  item: LaunchConfigurationsDTO.Responses.GetSingle.T;
  isLast: boolean;
  setError?: (error: Option<Error>) => void;
  allFields?: Array<Field.Field<string>>;
}

export function useHandleSubmitCallback({
  defaultValues,
  item,
  isLast,
  setError,
  allFields,
}: HandleSubmitCallArgs) {
  const authedFields = useAuthedFields();
  const { i18n } = useTranslation();
  const fileLoader = useFileLoader();
  const { state } = useContext(ConfigContext);

  return useCallback(
    async (data) => {
      if (!isLast) {
        return;
      }

      // If we're at the last page, validation includes submittal
      setError?.(None);
      try {
        const captchaToken = await captcha();

        let newData = {
          ...defaultValues,
          ...data,
          user: {
            ...data.user,
            locale: i18n.language.replaceAll("-", "_"),
          },
          captchaToken,
        } as FormType;

        // remove initial data if the user is verified
        if (state.userKycStatus === "VERIFIED") {
          unset(newData, "user.names.first");
          unset(newData, "user.names.last");
          unset(newData, "user.dob");
          unset(newData, "user.nationality");
        }

        newData = deepOmitBy(
          newData,
          (val) => val === "" || val === null
        ) as FormType;

        if (
          item.configuration.merchantConfig.preVerifiedSourceWallet &&
          (!newData.sourceWallet?.address || !newData.sourceWallet?.chain)
        ) {
          throw new Error("source-wallet-is-missing");
        }

        return await fileLoader(
          ParticipationsService.postParticipation(
            item.id,
            newData,
            authedFields,
            allFields
          )
        );
      } catch (e: any) {
        setError?.(Some(e));
        return;
      }
    },
    [
      fileLoader,
      isLast,
      item.id,
      setError,
      defaultValues,
      authedFields,
      i18n.language,
    ]
  );
}
